import {API_URL, JSON_URL, GLOBAL_API} from "modules/constants";
import {
	IApiResponse,
	IBackdoorPayload,
	IContest,
	IGetQuestionRequestPayload,
	IPlayer,
	ISaveAnswerRequestPayload,
	IServerPrediction,
	IShowMyResponse,
	ISquad,
	IWithContest,
	TCheckTermsResponse,
	TCheckUserName,
	TUserPayload,
	TUserResponse,
} from "modules/types";
import {withTimeStamp} from "modules/utils/helpers";
import {
	IFetchDailyLeaderboard,
	IFetchLeaderboard,
	ILeaderboardResponse,
	IUserProfilePayload,
	IUserProfileResponse,
	ISuccessPrompt,
} from "modules/actions";
import {HttpClientService} from "modules/utils/HTTP";
import {set} from "lodash";
import {CANCEL} from "redux-saga";
import axios, {AxiosRequestConfig, InternalAxiosRequestConfig} from "axios";
import {ContextualProjects} from "modules/enums";
import {MLBStorage} from "modules/utils/Storage";

class HTTPClient extends HttpClientService {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 * http://fe-common-utils.s3-website-eu-west-1.amazonaws.com/classes/httpclient.html
	 */
	public request<T>(config: AxiosRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const request = super.request<T>({
			...config,
			cancelToken: source.token,
		});

		return set<Promise<T>>(request, CANCEL, () => source.cancel());
	}
}

// const onCatchNetworkError = ({response, message = "Network error"}: AxiosError<IApiResponse>) => {
// 	const data = response?.data;
// 	const error = isObject(data)
// 		? {
// 				...data,
// 				errors: data.errors.map((e) => new ApiError(e.message, response?.status)),
// 		  }
// 		: {
// 				errors: [new ApiError(message, response?.status)],
// 		  };
//
// 	return Promise.reject(error).catch((err) => ApiError.CHECK(err as IApiResponse));
// };

export const APIClient = new HTTPClient({
	baseURL: API_URL,
	withCredentials: true,
	// onCatchNetworkError,
});

const xSidInterceptor = (config: InternalAxiosRequestConfig) => {
	const xSid = MLBStorage.GET("xSid") || "";
	if (xSid.length > 2) {
		set(config, "params.xSid", xSid);
	}
	return config;
};

export const GlobalAPIClient = new HTTPClient({
	baseURL: GLOBAL_API,
	withCredentials: true,
	// onCatchNetworkError,
});

APIClient.interceptors.request.use(xSidInterceptor);
GlobalAPIClient.interceptors.request.use(xSidInterceptor);

export const JSONClient = new HTTPClient({
	baseURL: JSON_URL,
});

type TUserProfileResponse = IApiResponse<{gameProfile: IUserProfileResponse}>;

export const Api = {
	JSON: {
		checksums: () => JSONClient.get(withTimeStamp("checksum.json")),
		contests: () => JSONClient.get<{contests: IContest[]}>(withTimeStamp("contests.json")),
		players: () => JSONClient.get<{players: IPlayer[]}>(withTimeStamp("players.json")),
		squads: () => JSONClient.get<{squads: ISquad[]}>(withTimeStamp("squads.json")),
	},
	Auth: {
		backdoor: (params: IBackdoorPayload) =>
			APIClient.post<TUserResponse>(`/auth/backdoor`, params),
		login: (params: TUserPayload) => APIClient.post<TUserResponse>("/auth/login", params),
		logout: () => APIClient.post<IApiResponse>("/auth/logout"),
	},
	User: {
		session: () => APIClient.get<TUserResponse>("/user"),
		check_terms: (uid: string) =>
			APIClient.get<TCheckTermsResponse>(`/user/check_terms/${String(uid)}`),
		check_username: (uid: string) =>
			APIClient.get<TCheckUserName>(`/user/check_username/${String(uid)}`),
		sendWhatsNewModalViewed: () => APIClient.post<IApiResponse>("/user/view_whats_new_modal"),
		sendTutorialModalViewed: () => APIClient.post<IApiResponse>("user/view_tutorial"),
		sendThanksForPlayingViewed: () =>
			APIClient.post<IApiResponse>("user/view_thanks_for_playing_modal"),

		checkContextualPrompt: ({uid}: {uid: string}) =>
			GlobalAPIClient.get<IApiResponse<ISuccessPrompt>>(
				`global/prompt/${uid}/${ContextualProjects.BY_THE_NUMBER}`
			),

		declineContextualPrompt: ({uid}: {uid: string}) =>
			GlobalAPIClient.post<IApiResponse>(
				`global/user_decline/${uid}/${ContextualProjects.BY_THE_NUMBER}`
			),
	},
	Contest: {
		getQuestion: (data: IGetQuestionRequestPayload) =>
			APIClient.get<IApiResponse<IServerPrediction>>(
				`prediction/${data.contestId}/${data.predictionId}`
			),
		saveAnswer: (data: IWithContest<ISaveAnswerRequestPayload>) =>
			APIClient.post<IApiResponse<IServerPrediction>>(
				`prediction/${data.contestId}`,
				data.payload
			),
		showMy: (contestId: number) =>
			APIClient.get<IApiResponse<IShowMyResponse>>(`prediction/show_my/${contestId}`),
		clearAnswers: (contestId: number) =>
			APIClient.post<IApiResponse<unknown>>(`prediction/${contestId}/clear`),
	},
	Ranking: {
		getYesterdayLeaderboard: (params: IFetchLeaderboard) =>
			APIClient.get<IApiResponse<ILeaderboardResponse>>("ranking/yesterday-leaderboard", {
				params,
			}),
		getDailyLeaderboard: ({contestId, ...params}: IFetchDailyLeaderboard) =>
			APIClient.get<IApiResponse<ILeaderboardResponse>>(
				`ranking/contest-leaderboard/${contestId}`,
				{params}
			),
		getAllTimeLeaderboard: (params: IFetchLeaderboard) =>
			APIClient.get<IApiResponse<ILeaderboardResponse>>("ranking/all-time-leaderboard", {
				params,
			}),
		userProfile: ({userId}: IUserProfilePayload) =>
			APIClient.get<TUserProfileResponse>(`ranking/game-profile/${userId}`),
	},
};

export * from "./ApiError";

export default Api;
